import React, { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchVentasDiarias,
  setVentasDiariasFilters,
  setVentasDiariasPagination,
  clearVentasDiariasFilters,
} from "../../../slices/biSlice";
import usePolling from "../../../hooks/usePolling";
import { formatColones, formatColonesCompact } from "../../../utils/biCalculations";
import KPICard from "../../../components/BI/KPICard";
import DataTable from "../../../components/BI/DataTable";
import FilterBar from "../../../components/BI/FilterBar";
import ExportButtons from "../../../components/BI/ExportButtons";
import PollingIndicator from "../../../components/BI/PollingIndicator";

const baseUrl = process.env.REACT_APP_API_URL;

/**
 * Obtiene la fecha de hoy en formato YYYY-MM-DD.
 */
function getTodayDate() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Columnas de la tabla de ventas diarias.
 */
const columns = [
  { key: "numeroOrden", label: "# Orden", sortable: true },
  { key: "cliente", label: "Cliente", sortable: true },
  { key: "telefono", label: "Teléfono", render: (row) => {
    const tel = String(row.telefono || "");
    return tel === "88888888" ? "—" : tel || "—";
  }},
  {
    key: "monto",
    label: "Monto",
    sortable: true,
    render: (row) => formatColones(row.monto),
  },
  { key: "estadoPago", label: "Estado Pago", sortable: true },
  { key: "metodoPago", label: "Método Pago" },
  { key: "metodoEntrega", label: "Método Entrega" },
  {
    key: "fecha",
    label: "Fecha",
    sortable: true,
    render: (row) => {
      if (!row.fecha) return "—";
      const date = new Date(row.fecha);
      return date.toLocaleString("es-CR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
];

/**
 * Configuración de filtros del FilterBar.
 */
const filterConfig = [
  {
    name: "fecha",
    label: "Fecha",
    type: "date",
  },
  {
    name: "tienda",
    label: "Tienda",
    type: "select",
    options: [
      { value: "1", label: "Heredia" },
      { value: "2", label: "Guácimo" },
      { value: "3", label: "Santa Ana" },
    ],
  },
  {
    name: "estadoPago",
    label: "Estado Pago",
    type: "select",
    options: [
      { value: "pagado", label: "Pagado" },
      { value: "pendiente", label: "Pendiente" },
    ],
  },
  {
    name: "metodoPago",
    label: "Método Pago",
    type: "select",
    options: [
      { value: "efectivo", label: "Efectivo" },
      { value: "tarjeta", label: "Tarjeta" },
      { value: "sinpe", label: "SINPE" },
      { value: "transferencia", label: "Transferencia" },
    ],
  },
];

/**
 * Determina la tendencia (trend) para la variación porcentual.
 */
function getVariacionTrend(variacion) {
  if (variacion === "N/A" || variacion == null) return "neutral";
  if (Number(variacion) > 0) return "up";
  if (Number(variacion) < 0) return "down";
  return "neutral";
}

/**
 * Formatea la variación para mostrar como texto.
 */
function formatVariacion(variacion) {
  if (variacion === "N/A" || variacion == null) return "N/A";
  const num = Number(variacion);
  const prefix = num > 0 ? "+" : "";
  return `${prefix}${num.toFixed(1)}%`;
}

export function VentasDiariasPage() {
  const dispatch = useDispatch();
  const { indicadores, data, total, loading, error, filters, pagination } =
    useSelector((state) => state.bi.ventasDiarias);

  // Setear fecha de hoy como default al montar si no tiene valor
  useEffect(() => {
    if (!filters.fecha) {
      dispatch(setVentasDiariasFilters({ fecha: getTodayDate() }));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Polling: fetch automático cada 15 min
  const fetchData = useCallback(() => {
    return dispatch(fetchVentasDiarias());
  }, [dispatch]);

  const { lastUpdated, isStale, refetch } = usePolling(fetchData);

  // Re-fetch cuando cambian filtros o paginación
  useEffect(() => {
    if (filters.fecha) {
      dispatch(fetchVentasDiarias());
    }
  }, [dispatch, filters, pagination]);

  // Handler de cambio de filtro
  const handleFilterChange = (name, value) => {
    dispatch(setVentasDiariasFilters({ [name]: value || null }));
  };

  // Handler de limpiar filtros
  const handleClearFilters = () => {
    dispatch(clearVentasDiariasFilters());
    dispatch(setVentasDiariasFilters({ fecha: getTodayDate() }));
  };

  // Handler de cambio de página
  const handlePageChange = (skip) => {
    dispatch(setVentasDiariasPagination({ skip }));
  };

  // Variación porcentual
  const variacion = indicadores?.variacionPorcentual;
  const variacionTrend = getVariacionTrend(variacion);

  // URL de exportación con filtros actuales
  const exportUrl = `${baseUrl}/bi/ventas-diarias/export/excel?fecha=${
    filters.fecha || getTodayDate()
  }${filters.tienda ? `&tienda=${filters.tienda}` : ""}${
    filters.estadoPago ? `&estadoPago=${filters.estadoPago}` : ""
  }${filters.metodoPago ? `&metodoPago=${filters.metodoPago}` : ""}`;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-gray-900">
          Dashboard de Ventas Diarias
        </h1>
        <div className="flex items-center gap-3">
          <PollingIndicator
            lastUpdated={lastUpdated}
            isStale={isStale}
            onRefresh={refetch}
          />
          <ExportButtons exportUrl={exportUrl} disabled={loading || total === 0} />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Filtros (above KPIs since they affect the indicators) */}
      <FilterBar
        filters={filterConfig}
        values={{
          fecha: filters.fecha || getTodayDate(),
          tienda: filters.tienda || "",
          estadoPago: filters.estadoPago || "",
          metodoPago: filters.metodoPago || "",
        }}
        onChange={handleFilterChange}
        onClear={handleClearFilters}
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
        <KPICard
          title="Ventas Hoy"
          value={formatColonesCompact(indicadores?.ventasHoy)}
          loading={loading && !indicadores}
        />
        <KPICard
          title="Ventas Ayer"
          value={formatColonesCompact(indicadores?.ventasAyer)}
          loading={loading && !indicadores}
        />
        <KPICard
          title="Variación"
          value={formatVariacion(variacion)}
          trend={variacionTrend}
          variation={
            variacionTrend === "up"
              ? "vs. ayer"
              : variacionTrend === "down"
              ? "vs. ayer"
              : undefined
          }
          loading={loading && !indicadores}
        />
        <KPICard
          title="Órdenes Hoy"
          value={indicadores?.ordenesHoy ?? 0}
          loading={loading && !indicadores}
        />
        <KPICard
          title="Ticket Promedio"
          value={formatColonesCompact(indicadores?.ticketPromedio)}
          loading={loading && !indicadores}
        />
        <KPICard
          title="Clientes Únicos"
          value={indicadores?.clientesUnicos ?? 0}
          loading={loading && !indicadores}
        />
      </div>

      {/* Tabla de Ventas */}
      <DataTable
        columns={columns}
        data={data}
        loading={loading}
        emptyMessage="No hay ventas registradas para los filtros seleccionados."
        pagination={{ limit: pagination.limit, skip: pagination.skip, total }}
        onPageChange={handlePageChange}
        pageOptions={[50]}
      />
    </div>
  );
}

export default VentasDiariasPage;
